import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { Tab, Tabs, Tooltip, OverlayTrigger } from 'react-bootstrap';

import { connect } from 'react-redux';
import Helmet from 'react-helmet';
import Fa from 'react-fontawesome';
import OpportunityTable from '../../components/table/opportunity-table';
import Overview from '../../components/graphs/overview';
import OpportunityGraphs from '../../components/graphs/opportunity-graphs';
import MapChart from '../../components/map-chart';
import RateGraph from '../../components/rate-graph/rate-graph';
import PursuitLengthOutcomeGraphs from '../../components/pursuit-length-outcome-graphs';
import PursuitLengthOverTimeGraphs from '../../components/pursuit-length-over-time-graphs';
import StageGraph from '../../components/stage-graph';
import IndustryGraph from '../../components/industry-graph';
import requiredSetupMappings from '../../../../components/required-setup-mappings';
import requiredSync from '../../../../components/required-sync';
import NumberDisplay from '../../../../components/number-display';
import OpportunityModal from '../../components/opportunity-modal';
import orgAwearness from '../../../../components/organization-awearness';
import ProgressBar from '../../../../components/progress-bar';
import SearchForm from '../../../../components/search-form';
import AccountSize from '../../components/account-size';
import styles from './styles.css';
import { clearCache } from '../../../../components/dynamic-cell/actions';
import RequestFeedbackManager from '../../../../components/request-feedback-manager';
import RunTestRenderer from '../../../../components/table-cells/RunTestRenderer';
import AutomationTestModal from '../../../Setup/pages/DiscoveryBotPage/AutomationTestModal';

import {
  getCanSetupOrganization,
} from '../../../Auth/AuthReducer';

// Import Actions
import { fetchOpportunities, resetOpportunities } from '../../AnalysisActions';

// Import Selectors
import { getOpportunities, getLoading, getLoaded, getErrors, getHasErrors } from '../../AnalysisReducer';


class AnalysisPage extends Component {
  constructor() {
    super();
    this.state = {
      closeDateRange: [],
      selectedOpportunityIds: [],
      selectedTab: 'time',
      showTestModal: false,
      testOwnerId: null,
      testOpportunityId: null,
      testAutomationId: null,
    };
    this.onSearchChange = this.onSearchChange.bind(this);
    this.onReset = this.onReset.bind(this);
    this.handleCloseDateRangeChange = this.handleCloseDateRangeChange.bind(this);
    this.handleSelectOpportunityIds = this.handleSelectOpportunityIds.bind(this);
    this.onTabSelect = this.onTabSelect.bind(this);
    this.getColDefs = this.getColDefs.bind(this);
    this.requestRunTest = this.requestRunTest.bind(this);
    this.hideTestModal = this.hideTestModal.bind(this);
  }

  componentDidMount() {
    clearCache(this.props.dispatch);
    this.props.dispatch(resetOpportunities()).then(() => {
      this.mounted = true;
    });
  }

  componentWillUnmount() {
    this.mounted = false;
  }

  onSearchChange(criteria) {
    if (this.mounted) {
      this.props.dispatch(fetchOpportunities(criteria)).then((results) => {
        const { opportunities, total } = results;
        const returnResults = opportunities.length;
        if (total && returnResults < total) {
          const el = (
            <div>
              Your search return more than <NumberDisplay value={total} precision={0} /> results.<br />
              For optimal performance, only the latest <NumberDisplay value={returnResults} precision={0} /> will be displayed.
            </div>
          );
          this.context.addNotification(el, { type: 'warning', autoClose: 30 * 1000 });
        }
      });
    }
  }

  getColDefs(column) {
    if (column.tag === 'runTest') {
      if (this.props.canSetupOrg) {
        return {
          colId: column._id,
          headerName: column.label,
          width: column.width,
          hide: !column.is_visible,
          // field: 'opportunity_id',
          enableRowGroup: false,
          enablePivot: false,
          cellRenderer: 'runTestRenderer',
          cellRendererParams: {
            column,
            requestRunTest: this.requestRunTest,
          },
        };
      } else {
        return OpportunityTable.NULL_COLUMN;
      }
    }
    return null;
  }

  requestRunTest(automation, opportunity) {
    this.setState({ showTestModal: true, testOwnerId: opportunity.owner_id, testOpportunityId: opportunity.opportunity_id, testAutomationId: automation._id });
  }

  hideTestModal() {
    this.setState({ showTestModal: false }, () => {
      this.setState({
        testOwnerId: null,
        testOpportunityId: null,
      });
    });
  }

  onTabSelect(selectedTab) {
    this.setState({ selectedTab });
  }

  onReset() {
    this.props.dispatch(resetOpportunities());
  }

  handleCloseDateRangeChange(closeDateRange) {
    this.setState({ closeDateRange });
  }

  handleSelectOpportunityIds(selectedOpportunityIds) {
    this.setState({ selectedOpportunityIds });
  }

  render() {
    const {
      loading, loaded, hasErrors, opportunities, errors,
    } = this.props;
    const { closeDateRange } = this.state;

    const timePopover = (
      <Tooltip id="time-tooltip">
        Outcome rates over time
      </Tooltip>
    );

    const sizePopover = (
      <Tooltip id="size-tooltip">
        Outcome rates by deal size bucketed into small, medium and large deals.
      </Tooltip>
    );

    const geoPopover = (
      <Tooltip id="size-tooltip">
        Outcome rates by geographic regions.
      </Tooltip>
    );

    const pursuitLengthPopover = (
      <Tooltip id="size-tooltip">
        Outcome rates by length of time the opportunity was pursued bucketed into short, medium, and long periods.
      </Tooltip>
    );


    const pursuitTimePopover = (
      <Tooltip id="pursuit-over-time-tooltip">
        Outcome rates by length of time the opportunity was pursued over time.
      </Tooltip>
    );
    const stagePopover = (
      <Tooltip id="stage-tooltip">
        Outcome rates by sales stage.
      </Tooltip>
    );

    const industryPopover = (
      <Tooltip id="industry-tooltip">
        Outcome rates by industry.
      </Tooltip>
    );

    const accountSizePopover = (
      <Tooltip id="account-size-tooltip">
        Outcome rates by account size bucketed into small, medium, and large accounts.
      </Tooltip>
    );

    return (
      <div>
        <Helmet title="Outcome Rates" />
        <div className="container">
          <Helmet title="Outcome Rates" />
          <div className="clearfix">
            <div className="pull-right page-header-control">
              <SearchForm
                onSearchChange={this.onSearchChange}
                onCloseDateRangeChange={this.handleCloseDateRangeChange}
                onReset={this.onReset}
                disabled={loading}
                loadRecommendedCriteria
                pullRight
                type="outcomes"
              />
            </div>
            <h1 className="page-header">
              Outcome Rates
            </h1>
          </div>
          {loading && <div><ProgressBar /></div>}
          {!loading && hasErrors && <div className="alert alert-danger"><Fa name="exclamation-triangle" /> {errors.message}</div>}
        </div>
        {loaded &&
          <div>
            {opportunities.length === 0 &&
              <div className="container">
                <div className="alert alert-warning">
                  <p><strong><Fa name="exclamation-triangle" /> No records found matching your criteria.</strong></p>
                </div>
              </div>
            }
            {opportunities.length > 0 &&
              <div>
                <Overview />
                <div className={styles.graphWrapper}>
                  <div className="container">
                    <Tabs
                      defaultActiveKey={this.state.selectedTab}
                      id="outcome-tabs"
                      animation
                      mountOnEnter
                      unmountOnExit
                      onSelect={this.onTabSelect}
                    >
                      <Tab
                        eventKey="time"
                        title={
                          <span>
                            <OverlayTrigger trigger={['hover', 'focus']} placement="top" overlay={timePopover}>
                              <span>Time</span>
                            </OverlayTrigger>
                          </span>
                        }
                      >
                        <RateGraph
                          maxValue={1000}
                          closeDateRange={closeDateRange}
                          onBarSelect={this.handleSelectOpportunityIds}
                        />
                      </Tab>
                      <Tab
                        eventKey="pursuitTime"
                        title={
                          <span>
                            <OverlayTrigger trigger={['hover', 'focus']} placement="top" overlay={pursuitTimePopover}>
                              <span>Pursuit Time</span>
                            </OverlayTrigger>
                          </span>
                        }
                      >
                        <PursuitLengthOverTimeGraphs
                          maxValue={1000}
                          onPointSelect={this.handleSelectOpportunityIds}
                          closeDateRange={closeDateRange}
                        />
                      </Tab>
                      <Tab
                        eventKey="dealSize"
                        title={
                          <span>
                            <OverlayTrigger trigger={['hover', 'focus']} placement="top" overlay={sizePopover}>
                              <span>Deal Size</span>
                            </OverlayTrigger>
                          </span>
                        }
                      >
                        <OpportunityGraphs
                          onChartSegmentSelect={this.handleSelectOpportunityIds}
                        />
                      </Tab>
                      <Tab
                        eventKey="accountSize"
                        title={
                          <span>
                            <OverlayTrigger trigger={['hover', 'focus']} placement="top" overlay={accountSizePopover}>
                              <span>Account Size</span>
                            </OverlayTrigger>
                          </span>
                        }
                      >
                        <AccountSize
                          onChartSegmentSelect={this.handleSelectOpportunityIds}
                        />
                      </Tab>
                      <Tab
                        eventKey="pursuitLength"
                        title={
                          <span>
                            <OverlayTrigger trigger={['hover', 'focus']} placement="top" overlay={pursuitLengthPopover}>
                              <span>Pursuit Length</span>
                            </OverlayTrigger>
                          </span>
                        }
                      >
                        <PursuitLengthOutcomeGraphs
                          onChartSegmentSelect={this.handleSelectOpportunityIds}
                        />
                      </Tab>
                      <Tab
                        eventKey="geo"
                        title={
                          <span>
                            <OverlayTrigger trigger={['hover', 'focus']} placement="top" overlay={geoPopover}>
                              <span>Geography</span>
                            </OverlayTrigger>
                          </span>
                        }
                      >
                        <MapChart />
                      </Tab>
                      <Tab
                        eventKey="stage"
                        title={
                          <span>
                            <OverlayTrigger trigger={['hover', 'focus']} placement="top" overlay={stagePopover}>
                              <span>Stage</span>
                            </OverlayTrigger>
                          </span>
                        }
                      >
                        <StageGraph onBarSelect={this.handleSelectOpportunityIds} />
                      </Tab>
                      <Tab
                        eventKey="industry"
                        title={
                          <span>
                            <OverlayTrigger trigger={['hover', 'focus']} placement="top" overlay={industryPopover}>
                              <span>Industry</span>
                            </OverlayTrigger>
                          </span>
                        }
                      >
                        <IndustryGraph onBarSelect={this.handleSelectOpportunityIds} />
                      </Tab>
                    </Tabs>
                  </div>
                </div>
                <div className="container">
                  <OpportunityModal
                    opportunityIds={this.state.selectedOpportunityIds}
                    getOpportunities={getOpportunities}
                    onClose={() => {
                      this.setState({ selectedOpportunityIds: [] });
                    }}
                    tableConfigs={{
                      tableName: 'outcomes-table',
                      getColDefs: this.getColDefs,
                      frameworkComponents: {
                        runTestRenderer: RunTestRenderer,
                      },
                    }}
                  />
                  <OpportunityTable
                    opportunities={opportunities}
                    tableName="outcomes-table"
                    title="Opportunity Details"
                    getColDefs={this.getColDefs}
                    frameworkComponents={{
                      runTestRenderer: RunTestRenderer,
                    }}
                  />
                  {this.props.canSetupOrg &&
                    <AutomationTestModal
                      open={this.state.showTestModal}
                      onClose={this.hideTestModal}
                      ownerId={this.state.testOwnerId}
                      opportunityId={this.state.testOpportunityId}
                      automationId={this.state.testAutomationId}
                    />
                  }
                </div>
              </div>
            }
          </div>
        }
        <RequestFeedbackManager />
      </div>
    );
  }
}

// Retrieve data from store as props
function mapStateToProps(state) {
  return {
    opportunities: getOpportunities(state),
    loading: getLoading(state),
    loaded: getLoaded(state),
    errors: getErrors(state),
    hasErrors: getHasErrors(state),
    canSetupOrg: getCanSetupOrganization(state),
  };
}

AnalysisPage.propTypes = {
  opportunities: PropTypes.arrayOf(PropTypes.shape({
    opportunity_id: PropTypes.string.isRequired,
  })).isRequired,
  loading: PropTypes.bool.isRequired,
  loaded: PropTypes.bool.isRequired,
  errors: PropTypes.shape({
    message: PropTypes.string.isRequired,
  }),
  dispatch: PropTypes.func.isRequired,
  hasErrors: PropTypes.bool.isRequired,
  canSetupOrg: PropTypes.bool,
};

AnalysisPage.defaultProps = {
  errors: null,
  canSetupOrg: false,
};
AnalysisPage.contextTypes = {
  router: PropTypes.object,
  addNotification: PropTypes.func.isRequired,
};
export default connect(mapStateToProps)(orgAwearness(requiredSync(requiredSetupMappings(AnalysisPage, ['stage']))));
